// Imported from the module rather than the `@/models` barrel: the barrel
// re-exports this file, so going through it makes the class extend an
// as-yet-undefined `Model` whenever this module is loaded first.
import { Model } from './model';
import { DateTime } from 'luxon';
// Imported from the module, not the `@/utils` barrel: the barrel re-exports
// `localStorageUtils`, which imports `@/models` — going through it would pull
// this file into the same cycle the note above describes.
import { deepLinkStudyId } from '@/utils/generalUtils';

/**
 * One notification this user has received.
 *
 * Backed by the API's `user_notification` table, which is an append-only log —
 * deliberately not the `notification` table, which is a scheduler queue: it holds
 * unsent future rows, is hard-deleted when a case reschedules, and its
 * `fulfilledAt` already means "the task behind this was completed". `readAt` here
 * means only that the person opened the notification centre and saw it.
 */
export interface NetworkNotification {
  id: string;
  title: string;
  body: string | null;
  url: string | null;
  channel: string;
  source: string;
  ruleKey: string | null;
  studyId: string | null;
  caseId: string | null;
  formId: string | null;
  sentAt: string;
  readAt: string | null;
}

export interface NotificationPage {
  notifications: NotificationModel[];
  /**
   * The last row's `sentAt`, or null when the page was not full.
   *
   * Keyset, not offset: an inbox grows at the head, so an offset page shifts
   * under the reader every time something arrives.
   */
  nextCursor: string | null;
}

interface INotification {
  id: string;
  title: string;
  body?: string;
  url?: string;
  channel: string;
  source: string;
  ruleKey?: string;
  studyId?: string;
  caseId?: string;
  formId?: string;
  sentAt: Date;
  readAt?: Date;
}

export class NotificationModel extends Model {
  id: string;
  title: string;
  body?: string;
  /**
   * The absolute deep link as delivered. It is kept verbatim here; the view
   * turns it into an app route with `transformDeepLink` when the row is opened.
   */
  url?: string;
  channel: string;
  source: string;
  ruleKey?: string;
  studyId?: string;
  caseId?: string;
  formId?: string;
  sentAt: Date;
  readAt?: Date;

  constructor({
    id,
    title,
    body,
    url,
    channel,
    source,
    ruleKey,
    studyId,
    caseId,
    formId,
    sentAt,
    readAt
  }: INotification) {
    super();
    this.id = id;
    this.title = title;
    this.body = body;
    this.url = url;
    this.channel = channel;
    this.source = source;
    this.ruleKey = ruleKey;
    this.studyId = studyId;
    this.caseId = caseId;
    this.formId = formId;
    this.sentAt = sentAt;
    this.readAt = readAt;
  }

  get isUnread(): boolean {
    return !this.readAt;
  }

  static mapFromBackend(networkNotification: NetworkNotification): NotificationModel {
    return new NotificationModel({
      id: networkNotification.id,
      title: networkNotification.title,
      body: networkNotification.body ?? undefined,
      url: networkNotification.url ?? undefined,
      channel: networkNotification.channel,
      source: networkNotification.source,
      ruleKey: networkNotification.ruleKey ?? undefined,
      studyId: networkNotification.studyId ?? undefined,
      caseId: networkNotification.caseId ?? undefined,
      formId: networkNotification.formId ?? undefined,
      sentAt: DateTime.fromISO(networkNotification.sentAt).toJSDate(),
      readAt: networkNotification.readAt ?
        DateTime.fromISO(networkNotification.readAt).toJSDate() :
        undefined
    });
  }

  /**
   * Built here rather than in the component so the live-push path and the fetched
   * list format the same way — a toast and a list row are the same event.
   */
  static fromPushPayload(payload: Record<string, string>, title: string, body: string): NotificationModel {
    return new NotificationModel({
      // A push carries no row id, so it is keyed on the notification id the API
      // stamps into the payload, falling back to the rule and the moment.
      id: payload.notificationId ?? `${payload.ruleKey ?? 'push'}:${Date.now()}`,
      title,
      body,
      url: payload.url,
      channel: 'push',
      source: payload.source ?? 'push',
      ruleKey: payload.ruleKey,
      // Read off the deep link when the payload does not name it outright: the
      // notification centre is study-scoped, so it needs to know which study a
      // push belongs to before showing it.
      studyId: payload.studyId ?? (payload.url ? deepLinkStudyId(payload.url) : undefined),
      caseId: payload.caseId,
      formId: payload.formId,
      sentAt: new Date()
    });
  }
}
